import { useEffect, useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { emptyObject } from '@constants/empty';
import useRequestStatusStore from '@stores/requestStatus';
import { getFetchingUpdater } from '@stores/selectors/requestStatus';
import { plannerTypes } from '@actions-constants/index';
import messageTypes from '@cont/PlannerV2/workers/main/planner.types';
import isEqual from 'lodash/isEqual';
import usePlannerWorker from '@cont/PlannerV2/planner-hooks/usePlannerWorker';
import useFeaturesAndBrowseCardStore from '@stores/planner/featuresAndBrowseCardStore';
import {
  getFeatureCardsSetter,
  getFeatureCards,
  getShowBrowseSetter,
  getDataSetterV2,
  getIsFinalSetupCompleteSetterV2,
  getCalendarDateV2
} from '@stores/selectors/planner';
import { useInfoStoreV2 } from '@stores/planner/infoStore';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import useTopology from '@api/hooks/topology/useTopology';
import { useHelperFlagsStoreV2 } from '@stores/planner/helperFlagsStore';
import { useCalendarStoreV2 } from '@stores/planner/calendarStore';
import {
  getDateKey,
  getStartOfTheDayInMs,
  getTodayDate
} from '../planner-utils/date-time-helper';
import fetchFeatureCards from './fetchFeatureCards';
import useAPIHandler from './useAPIHandler';
import useWorkerAPIHandler from './useWorkerAPIHandler';

export const initialResetInfo = {
  shouldReset: false,
  resetDayKey: ''
};

const usePlanner = (selectedDateKey) => {
  const {
    query: { goalUID }
  } = useRouter();
  const apiCaller = useAPIHandler();
  const userUID = useMyInfo(getMeUid);
  const [isInitialCheckComplete, setIsInitialCheckComplete] = useState(false);
  const [forceResetData, setForceResetData] = useState(false);

  const setFetching = useRequestStatusStore(getFetchingUpdater);
  const onInitialCheckComplete = useCallback(() => {
    if (!isInitialCheckComplete) {
      setIsInitialCheckComplete(true);
    }
  }, [isInitialCheckComplete]);

  const initialDate = useCalendarStoreV2(getCalendarDateV2());

  const currentFeatureCards = useFeaturesAndBrowseCardStore(
    getFeatureCards(goalUID)
  );
  const setFeatureCards = useFeaturesAndBrowseCardStore(getFeatureCardsSetter);
  const setShowBrowse = useFeaturesAndBrowseCardStore(getShowBrowseSetter);

  const { isPlatformGoal } = useTopology(goalUID) || emptyObject;

  const refreshFeatureCards = useCallback(async () => {
    const featureCards = await fetchFeatureCards(goalUID, apiCaller);
    const todayKey = getDateKey(getTodayDate().date);
    if (!isEqual(currentFeatureCards, featureCards)) {
      setFeatureCards(goalUID, featureCards, todayKey);
    }
  }, [setFeatureCards, goalUID, currentFeatureCards, apiCaller]);

  const refreshBrowseCard = useCallback(async () => {
    const todayKey = getDateKey(getTodayDate().date);
    if (isPlatformGoal) {
      setShowBrowse(goalUID, !isPlatformGoal, todayKey);
    }
  }, [setShowBrowse, goalUID, isPlatformGoal]);

  const { getUrlAndAllHeaders } = useWorkerAPIHandler();
  const { mainWorkerInstance, attachListeners, detachListeners } =
    usePlannerWorker();
  const isFinalSetupCompleteSetter = useHelperFlagsStoreV2(
    getIsFinalSetupCompleteSetterV2
  );
  const dataSetterV2 = useInfoStoreV2(getDataSetterV2);

  useEffect(() => {
    const dispatchAPIInProgressAction = () => {
      setFetching(true);
    };
    attachListeners({
      [messageTypes.FETCH_DAY_WISE_DATA_IN_PROGRESS]: () => {
        isFinalSetupCompleteSetter(false);
        dispatchAPIInProgressAction();
      },
      [messageTypes.FETCH_DAY_WISE_DATA_SUCCESS]: ({ data: newData }) => {
        setFetching(false);
        const todayKey = getDateKey(getTodayDate().date);
        if (todayKey === selectedDateKey) {
          if (newData.length < 2) {
            setShowBrowse(goalUID, true, todayKey);
          } else {
            setShowBrowse(goalUID, false, todayKey);
          }
        }
        dataSetterV2({
          plannerItemsByDays: newData
        });
        isFinalSetupCompleteSetter(true);
      },
      [messageTypes.INITIAL_CHECK_COMPLETE]: onInitialCheckComplete,
      [plannerTypes.SET_DATA_V2]: ({ data }) => {
        const { plannerData } = data;
        return dataSetterV2(plannerData);
      }
    });
    return () =>
      detachListeners([
        messageTypes.FETCH_DAY_WISE_DATA_IN_PROGRESS,
        messageTypes.FETCH_DAY_WISE_DATA_SUCCESS,
        messageTypes.INITIAL_CHECK_COMPLETE,
        plannerTypes.SET_DATA_V2
      ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalUID, detachListeners, attachListeners]);

  useEffect(() => {
    if (mainWorkerInstance && goalUID && userUID) {
      if (!isInitialCheckComplete) {
        mainWorkerInstance.postMessage({
          type: messageTypes.RUN_INITIAL_TASKS,
          data: {
            goalUID,
            userUID,
            shouldResetData: forceResetData,
            initialDate,
            forceResetData
          }
        });
      }
    }
  }, [
    goalUID,
    isInitialCheckComplete,
    userUID,
    forceResetData,
    mainWorkerInstance,
    initialDate
  ]);

  useEffect(() => {
    if (mainWorkerInstance && goalUID && userUID && selectedDateKey) {
      mainWorkerInstance.postMessage({
        type: messageTypes.FETCH_DAY_WISE_DATA,
        data: {
          goalUID,
          userUID,
          selectedDateKey,
          programmeApiInfo: getUrlAndAllHeaders({
            endpoint: 'sheldon/v1/planner/programme-info',
            params: {
              epoch: getStartOfTheDayInMs(selectedDateKey),
              goal_uid: goalUID
            }
          }),
          userProgrammeApiInfo: getUrlAndAllHeaders({
            endpoint: 'sheldon/v1/planner/user/info',
            params: {
              epoch: getStartOfTheDayInMs(selectedDateKey),
              goal_uid: goalUID
            }
          })
        }
      });
    }
  }, [
    goalUID,
    userUID,
    mainWorkerInstance,
    selectedDateKey,
    getUrlAndAllHeaders
  ]);

  useEffect(() => {
    const todayKey = getDateKey(getTodayDate().date);
    if (
      mainWorkerInstance &&
      goalUID &&
      userUID &&
      todayKey === selectedDateKey
    ) {
      refreshBrowseCard();
      refreshFeatureCards();
    }
  }, [
    goalUID,
    userUID,
    mainWorkerInstance,
    selectedDateKey,
    refreshBrowseCard,
    refreshFeatureCards
  ]);

  const handleResetData = () => setForceResetData(true);

  return {
    isInitialCheckComplete,
    refreshFeatureCards,
    handleResetData
  };
};

export default usePlanner;
